import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, ShoppingBasket } from "lucide-react";

const UNITS = [
  ["per_stay", "par séjour"],
  ["per_night", "par nuit"],
  ["per_person", "par personne"],
  ["per_person_per_night", "par personne et par nuit"],
];
const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const ReservationExtras = ({ cfg, setCfg, saveCfg, saving }) => {
  const extras = cfg.extras || [];

  const set = (i, patch) => setCfg({
    ...cfg, extras: extras.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
  });
  const add = () => setCfg({
    ...cfg,
    extras: [...extras, {
      id: "", label: "Draps", price: 8, unit: "per_person",
      description: "", max_qty: 10, active: true,
    }],
  });
  const remove = (i) => setCfg({ ...cfg, extras: extras.filter((_, idx) => idx !== i) });

  return (
    <div className="border border-[#DEE2E6] bg-white p-4" data-testid="cr-extras">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1">
          <ShoppingBasket className="h-3 w-3" />Options payantes
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid="cr-extra-add" className="ml-auto">
          <Plus className="h-4 w-4 mr-1" />Ajouter une option
        </Button>
      </div>

      {extras.length === 0 ? (
        <div className="border border-dashed border-[#DEE2E6] p-6 text-center text-xs text-[#868E96]"
             data-testid="cr-extras-empty">
          Aucune option. Ajoutez « Draps », « Ménage de fin de séjour » ou « Petit-déjeuner » :
          vos clients les cochent au moment de réserver et le montant s'ajoute au devis.
        </div>
      ) : (
        <div className="space-y-3" data-testid="cr-extras-list">
          {extras.map((e, i) => (
            <div key={e.id || i} className="border border-[#DEE2E6] p-3" data-testid={`cr-extra-${i}`}>
              <div className="grid md:grid-cols-[1fr_110px_1fr_100px_auto] gap-2 items-end">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nom</Label>
                  <Input value={e.label || ""} data-testid={`cr-extra-label-${i}`}
                         onChange={(ev) => set(i, { label: ev.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Prix</Label>
                  <Input type="number" step="0.01" value={e.price ?? 0} data-testid={`cr-extra-price-${i}`}
                         onChange={(ev) => set(i, { price: parseFloat(ev.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Facturé</Label>
                  <Select value={e.unit || "per_stay"} onValueChange={(v) => set(i, { unit: v })}>
                    <SelectTrigger data-testid={`cr-extra-unit-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Qté max</Label>
                  <Input type="number" min="1" value={e.max_qty ?? 1} data-testid={`cr-extra-maxqty-${i}`}
                         onChange={(ev) => set(i, { max_qty: parseInt(ev.target.value) || 1 })} />
                </div>
                <div className="flex gap-1">
                  <button type="button" data-testid={`cr-extra-active-${i}`}
                          onClick={() => set(i, { active: e.active === false })}
                          className={`h-10 px-3 text-[10px] uppercase font-bold border ${
                            e.active === false
                              ? "bg-white text-[#868E96] border-[#DEE2E6]"
                              : "bg-[#059669] text-white border-[#059669]"}`}>
                    {e.active === false ? "off" : "on"}
                  </button>
                  <Button variant="outline" size="icon" onClick={() => remove(i)}
                          data-testid={`cr-extra-del-${i}`} className="text-[#E60000]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-[10px] uppercase tracking-wider">Précision (facultatif)</Label>
                <Input value={e.description || ""} placeholder="Livrés le jour de l'arrivée"
                       data-testid={`cr-extra-desc-${i}`}
                       onChange={(ev) => set(i, { description: ev.target.value })} />
              </div>
              <div className="text-[11px] text-[#495057] mt-2" data-testid={`cr-extra-preview-${i}`}>
                Exemple : 7 nuits, 2 personnes →{" "}
                <b>{eur((Number(e.price) || 0) * ({ per_stay: 1, per_night: 7, per_person: 2, per_person_per_night: 14 }[e.unit] || 1))}</b>
                {(e.max_qty || 1) > 1 ? <> · quantité choisie par le client (max {e.max_qty})</> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Button onClick={() => saveCfg()} disabled={saving} className="mt-4 bg-[#002FA7] text-white"
              data-testid="cr-extras-save">
        <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les options"}
      </Button>
    </div>
  );
};
